import { useEffect, useState } from "react";
import shortid from "shortid";
import {
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Box,
  Grid,
  TextField,
  CurrencyInput,
  Paper,
  DataGrid,
  TextFielDecimalNumbers,
  Loading,
} from "../../../../components";
import { formatPrice, responseErros, unformatPrice } from "../../../../utils";
import {
  maoObraListValidator,
  maoObraValidator,
} from "../middlewares/mao-obra-servico.validator";
import { InputErros } from "../../../../helpers";
import { MaoObraService, notification } from "../../../../services";

export function MaoObraServicoDialog({ open, setOpen }) {
  const [maosObra, setMaosObra] = useState([]);
  const [maoObra, setMaoObra] = useState({});
  const [isItemEdit, setIsItemEdit] = useState(false);
  const [idsRemoved, setIdsRemoved] = useState([]);
  const [loading, setLoading] = useState(false);
  const maoObraService = new MaoObraService();

  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  useEffect(() => {
    if (open) {
      buscarMaosObra();
    }
  }, [open]);

  async function buscarMaosObra() {
    setLoading(true);
    const result = await maoObraService.getAll();
    if (!result.isAxiosError) {
      setMaosObra(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const columns = [
    { field: "descricao", headerName: "Nome do Grupo", flex: 300 },
    {
      field: "custoMensal",
      headerName: "Custo Mensal",
      flex: 80,
      valueFormatter: (params) => `R$ ${formatPrice(params.value || 0, false)}`,
    },
    {
      field: "encargos",
      headerName: "Encargos",
      flex: 80,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => `${params.value || 0} %`,
    },
    {
      field: "horasProdutivas",
      headerName: "Horas Produtivas",
      flex: 80,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => `${params.value || 0} h`,
    },
    {
      field: "custoHora",
      headerName: "Custo/Hora",
      flex: 80,
      headerAlign: "right",
      align: "right",
      valueFormatter: (params) => `R$ ${formatPrice(params.value || 0, false)}`,
    },
  ];

  function onClose() {
    setMaoObra({});
    setMaosObra([]);
    setIdsRemoved([]);
    setIsItemEdit(false);
    setOpen(false);
  }

  function calcularCustoHora(custo, encargosPct, horas) {
    const custoNum = parseFloat(custo) || 0;
    const enc = parseFloat(encargosPct) || 0;
    const h = parseFloat(horas) || 1;
    return (custoNum + custoNum * (enc / 100)) / h;
  }

  function adicionar(item = maoObra) {
    const custoHora = calcularCustoHora(
      item.custoMensal,
      item.encargos,
      item.horasProdutivas,
    );
    const maoObraTemp = {
      id: item?.itemEditado ? item.id : shortid.generate(),
      descricao: item.descricao,
      custoMensal: parseFloat(item.custoMensal) || 0,
      encargos: parseFloat(item.encargos) || 0,
      horasProdutivas: parseFloat(item.horasProdutivas) || 1,
      custoHora,
    };

    maoObraValidator
      .validate(maoObraTemp, { abortEarly: false })
      .then(() => {
        if (item?.itemEditado) {
          setMaosObra((prev) =>
            prev.map((m) => (m.id === maoObraTemp.id ? maoObraTemp : m)),
          );
          setIsItemEdit(true);
        } else {
          setMaosObra((prev) => [maoObraTemp, ...prev]);
        }
        setMaoObra({});
        inputErros.limpar();
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  async function upsertMaoObra() {
    maoObraListValidator
      .validate(maosObra, { abortEarly: false })
      .then(async () => {
        setLoading(true);
        const body = { items: maosObra, idsRemoved };
        const result = await maoObraService.upsert(body);
        if (!result.isAxiosError) {
          notification.alteracaoSucesso();
          onClose();
        } else {
          responseErros(result);
        }
        setLoading(false);
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  function onClickEditServico(id) {
    const edit = maosObra.find((m) => m.id === id);
    if (edit) {
      setMaoObra({ ...edit, itemEditado: true });
    }
  }

  function onClickDeleteServico(id) {
    const removed = maosObra.find((m) => m.id === id);
    if (removed && typeof removed.id === "number") {
      setIdsRemoved((prev) => [...prev, removed.id]);
    }
    setMaosObra((prev) => prev.filter((m) => m.id !== id));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperComponent="div"
      PaperProps={{
        style: { borderRadius: "0.7rem", background: "#ffffff" },
      }}
    >
      <DialogContent>
        <CardHeader
          title="Novo Grupo de Mão de Obra"
          sx={{ padding: "1rem 1.6rem" }}
        />

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                id="descricao"
                name="descricao"
                label="Nome do Grupo"
                required
                value={maoObra.descricao || ""}
                onChange={(e) =>
                  setMaoObra({ ...maoObra, descricao: e.target.value })
                }
                fullWidth
                variant="outlined"
                error={inputErros.get("descricao")}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <CurrencyInput
                label="Custo Mensal"
                value={maoObra.custoMensal || 0}
                margin="none"
                onChange={(e) =>
                  setMaoObra({
                    ...maoObra,
                    custoMensal: unformatPrice(e.target.value),
                  })
                }
                fullWidth
                error={inputErros.get("custoMensal")}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextFielDecimalNumbers
                label="Encargos %"
                value={maoObra.encargos || 0}
                margin="none"
                onChange={(e) =>
                  setMaoObra({
                    ...maoObra,
                    encargos: unformatPrice(e.target.value),
                  })
                }
                fullWidth
                error={inputErros.get("encargos")}
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <TextField
                label="Horas Produtivas"
                type="number"
                value={maoObra.horasProdutivas || 1}
                onChange={(e) =>
                  setMaoObra({ ...maoObra, horasProdutivas: e.target.value })
                }
                fullWidth
                inputProps={{ min: 1 }}
                error={inputErros.get("horasProdutivas")}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2">Custo/Hora</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {`R$ ${formatPrice(
                    calcularCustoHora(
                      maoObra.custoMensal,
                      maoObra.encargos,
                      maoObra.horasProdutivas,
                    ),
                    false,
                  )}`}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => adicionar(maoObra)}
              >
                {maoObra.itemEditado ? "Atualizar" : "Adicionar"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
        <Box>
          <DataGrid
            rows={maosObra}
            columns={columns}
            onClickEdit={onClickEditServico}
            onClickDelete={onClickDeleteServico}
            editShow
            deleteShow
          />
        </Box>
      </DialogContent>
      <DialogActions className="me-2">
        <Button
          onClick={onClose}
          color="padrao"
          variant="contained"
          disableElevation
        >
          Fechar
        </Button>
        {((maosObra.length > 0 &&
          maosObra.some((m) => typeof m.id === "string")) ||
          isItemEdit ||
          idsRemoved?.length > 0) && (
          <Button
            onClick={upsertMaoObra}
            color="primary"
            variant="contained"
            disableElevation
          >
            Salvar
          </Button>
        )}
      </DialogActions>
      <Loading loading={loading} />
    </Dialog>
  );
}
